import React, { useState, useEffect } from 'react';
import {
  UserCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { authService } from '@/domains/internal/auth/services/authService';
import { userService } from '@/domains/internal/user-profile/services/userService';
import { Modal } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/Button';

interface ProfileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({ isOpen, onClose }) => {
  const user = authService.getUser();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user && user.user_type !== 'referral' && user.user_type !== 'referral_team') {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // This component is only for staff users
  if (!isOpen || !user || user.user_type === 'referral' || user.user_type === 'referral_team') return null;

  // Type assertion: at this point we know user is StaffUser
  const staffUser = user as import('@/shared/types/api').StaffUser;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await userService.updateProfile(formData);
      setSuccess('Profile updated successfully');
      setIsEditing(false);

      // Update local user data
      const updatedUser = { ...staffUser, ...response.data.staff };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Refresh the page after a short delay to show the updated info
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update profile');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    try {
      await userService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setSuccess('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to change password');
    }
  };

  // Get initials for avatar
  const initials = `${staffUser.first_name?.charAt(0) || ''}${staffUser.last_name?.charAt(0) || ''}`.toUpperCase();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Profile Settings"
      size="2xl"
    >
      <div>
        {/* Notifications */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* User Info */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold text-xl">
              {initials}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {staffUser.first_name} {staffUser.last_name}
              </h3>
              <p className="text-sm text-gray-500">{staffUser.email}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex space-x-8">
            <Button
              onClick={() => {
                setActiveTab('profile');
                setIsEditing(false);
                setError('');
                setSuccess('');
              }}
              variant="neutral"
              className={`!py-3 !border-b-2 !bg-transparent !rounded-none ${
                activeTab === 'profile'
                  ? '!border-[var(--color-primary)] !text-[var(--color-primary)]'
                  : '!border-transparent !text-gray-500 hover:!text-gray-700'
              }`}
              padding="py-3"
            >
              Profile Information
            </Button>
            <Button
              onClick={() => {
                setActiveTab('password');
                setError('');
                setSuccess('');
              }}
              variant="neutral"
              className={`!py-3 !border-b-2 !bg-transparent !rounded-none ${
                activeTab === 'password'
                  ? '!border-[var(--color-primary)] !text-[var(--color-primary)]'
                  : '!border-transparent !text-gray-500 hover:!text-gray-700'
              }`}
              padding="py-3"
            >
              Change Password
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'profile' ? (
            isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                      required
                    />
                  </div>
                </div>
                <div className="flex space-x-3 pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    padding="px-4 py-2"
                  >
                    Save Changes
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        first_name: user.first_name || '',
                        last_name: user.last_name || '',
                        email: user.email || '',
                        phone: user.phone || ''
                      });
                    }}
                    variant="neutral"
                    padding="px-4 py-2"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3 py-3">
                    <UserCircleIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Full Name</p>
                      <p className="text-gray-900">{user.first_name} {user.last_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 py-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="text-gray-900">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 py-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="text-gray-900">{user.phone || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="primary"
                  padding="px-4 py-2"
                >
                  Edit Profile
                </Button>
              </div>
            )
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  required
                  minLength={8}
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  padding="px-4 py-2"
                  className="flex items-center"
                >
                  <KeyIcon className="h-4 w-4 mr-2" />
                  Update Password
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setPasswordData({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                  }}
                  variant="neutral"
                  padding="px-4 py-2"
                >
                  Clear
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProfileSettingsModal;
